/**
 * CODE_COMMENTS_12
 */
import sortBy_ from 'lodash/sortBy'
import orderBy_ from 'lodash/orderBy'
import isArray_ from 'lodash/isArray'
import get_ from 'lodash/get'

import {
  withPropNormalized,
  withPropOrNormalized,
  withPropOfAllNormalized,
  withMultiplePropsOfAllNormalized,
  getEntireSlice as getEntireSliceCommon,
} from './higherOrderFunctions'

import {
  REDUCER_NAMES_ENTITIES,
  REDUCER_NAMES_ENTITIES_CUSTOMERS as defaultSlice,

  CUSTOMER_TYPES_MASTER,
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_CONTRACT_BREWER,
  CUSTOMER_TYPES_DISTRIBUTOR,
  CUSTOMER_TYPES_PUB,
  CUSTOMER_TYPES_SELF_DISTRIBUTION,

  CUSTOMER_STATUS_INACTIVE,
  CUSTOMER_STATUS_HOLD,

  CUSTOMER_HOLD_TYPE_CREDIT_HOLD,
  CUSTOMER_HOLD_TYPE_REPORTING_HOLD,

  CUSTOMER_REPS_ACCOUNT,
  CUSTOMER_REPS_CUSTOMER_EXPERIENCE,
  CUSTOMER_REPS_LOGISTICS,
  CUSTOMER_REPS_QUALITY,
  CUSTOMER_REPS_SALES,

  BUSINESS_UNIT_ID_TYPE_MICROSTAR,
} from '../../../constants'

import {
  deDupeArray,
} from '../../../utils'

import {
  NestedPropNotInObjectError,
} from '../../../customErrors'


/*
 * *****************************************************************************
 * The basics
 * *****************************************************************************
*/


// usage: you can use this to get the slice for all customers or for a single
// customer:
// const entireSliceForAllCustomers = getEntireSlice(state)
// const entireSliceForSingleCustomer = getEntireSlice(state, customerId)
export const getEntireSlice = getEntireSliceCommon(REDUCER_NAMES_ENTITIES, defaultSlice)

// usage: const customerType = getProp(state, customerId, 'customerType')
export const getProp = withPropNormalized(REDUCER_NAMES_ENTITIES, defaultSlice)

// usage: const customerType = getPropOr(state, customerId, 'customerType', 'customer type not found')
export const getPropOr = withPropOrNormalized(REDUCER_NAMES_ENTITIES, defaultSlice)

// usage: const arrayOfAllCustomerTypes = getPropOfAll(state, 'customerType')
export const getPropOfAll = withPropOfAllNormalized(REDUCER_NAMES_ENTITIES, defaultSlice)

// usage: const idAndCustomerTypeOfAllCustomers = getMultiplePropsOfAll(state, 'id', 'customerType')
// returns:
// [
//   {id: 1234, customerType: MASTER},
//   {id: 1235, customerType: BRW},
//   {id: 1236, customerType: DIST},
// ]
export const getMultiplePropsOfAll = withMultiplePropsOfAllNormalized(REDUCER_NAMES_ENTITIES, defaultSlice)


// The same selectors as above except instead of passing the whole Redux state
// in, you pass in the customers slice (the entire slice, all customers)

// usage: const customerType = getPropPassingInEntireCustomersSlice(entireCustomersSlice, customerId, 'customerType')
export const getPropPassingInEntireCustomersSlice = withPropNormalized()

/*
 * *****************************************************************************
 * Customer Types
 * *****************************************************************************
*/

// Usage: const answer = getIsCustomerAMasterType(entireCustomersSlice, customerId)
// Usage: const answer = getIsCustomerABrwOrConbrwOrDistType(entireCustomersSlice, customerId)
export const getIsCustomerAMasterType = isOneOfTheseHof(CUSTOMER_TYPES_MASTER)
export const getIsCustomerABrwType = isOneOfTheseHof(CUSTOMER_TYPES_BREWER)
export const getIsCustomerAConBrwType = isOneOfTheseHof(CUSTOMER_TYPES_CONTRACT_BREWER)
export const getIsCustomerADistType = isOneOfTheseHof(CUSTOMER_TYPES_DISTRIBUTOR)


export const getIsCustomerAMasterOrBrwOrConbrwOrDistType = isOneOfTheseHof(
  CUSTOMER_TYPES_MASTER,
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_CONTRACT_BREWER,
  CUSTOMER_TYPES_DISTRIBUTOR,
)
export const getIsCustomerAMasterOrBrwOrConbrwType = isOneOfTheseHof(
  CUSTOMER_TYPES_MASTER,
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_CONTRACT_BREWER,
)
export const getIsCustomerABrwOrConbrwType = isOneOfTheseHof(
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_CONTRACT_BREWER,
)
export const getIsCustomerABrwOrConbrwOrDistType = isOneOfTheseHof(
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_CONTRACT_BREWER,
  CUSTOMER_TYPES_DISTRIBUTOR,
)
export const getIsCustomerAMasterOrBrwType = isOneOfTheseHof(
  CUSTOMER_TYPES_MASTER,
  CUSTOMER_TYPES_BREWER,
)
export const getIsCustomerABrwOrDistType = isOneOfTheseHof(
  CUSTOMER_TYPES_BREWER,
  CUSTOMER_TYPES_DISTRIBUTOR,
)


// Brewers can have PUB children, see CODE_COMMENTS_49. Contract Brewers can
// have Self-Collection (COL) children.
export const getIsCustomerOfATypeThatHasChildren = (customers, customerId) => {
  if (getIsRentalCustomer(customers, customerId)) {
    return false
  }
  return getIsCustomerAMasterOrBrwOrConbrwType(customers, customerId)
}


export const getIsCustomerOfATypeThatCanHavePrelimInfoFetchedOnIt = (customers, customerId) => (
  !getIsCustomerStatusInactive(customers, customerId)
  && getIsCustomerAMasterOrBrwOrConbrwOrDistType(customers, customerId)
)


export const getIsCustomerOfATypeThatHasAddresses = (customers, customerId) => (
  getIsRentalCustomer(customers, customerId)
  || getIsCustomerABrwOrConbrwOrDistType(customers, customerId)
)


// related *to*, not related from. Shouldn't we also call /relatedTo on Contract
// Brewers? No, see CODE_COMMENTS_82. Distributors can be related to other
// distributors.
export const getIsCustomerOfATypeThatIsRelatedToOtherCustomers = (customers, customerId) => {
  if (getIsRentalCustomer(customers, customerId)) {
    return false
  }
  return getIsCustomerABrwOrDistType(customers, customerId)
}


export const getIsCustomerOfATypeThatIsRelatedFromOtherCustomers = (customers, customerId) => {
  if (getIsRentalCustomer(customers, customerId)) {
    return false
  }
  return getIsCustomerABrwOrConbrwOrDistType(customers, customerId)
}

// Even though Pubs and self-distribution customers are children of Master
// customedrs, they will not have their own portals (no one will ever log in
// with their root customer as a pub or self-distribution customer) and
// therefore will not have child cards in the "Children Overview" section of the
// Master dashboard.
export const getIsCustomerOfATypeThatHasItsOwnPortal = getIsCustomerABrwOrConbrwOrDistType

export const getIsCustomerOfATypeThatHasInboundUnacknowledgedShipments = (customers, customerId) => {
  if (getIsRentalCustomer(customers, customerId)) {
    return false
  }
  return getIsCustomerABrwOrConbrwType(customers, customerId)
}

// This includes rental customers (because all rentals are BRWs.)
export const getIsCustomerOfATypeThatHasContacts = getIsCustomerABrwOrConbrwOrDistType

export const getIsCustomerOfATypeThatOrdersCollars = (customers, customerId) => (
  getIsRentalCustomer(customers, customerId)
  || getIsCustomerABrwOrConbrwType(customers, customerId)
)

export const getIsCustomerOfATypeThatPaysBalances = (customers, customerId) => (
  getIsRentalCustomer(customers, customerId)
  || getIsCustomerABrwOrConbrwType(customers, customerId)
)

// helper functions for this section

// You can either pass in a single array of customer types or list each customer
// type as its own argument
function isOneOfTheseHof(...custTypes) {
  const arrayOfCustTypes = custTypes.reduce(
    (acc, argument) => (isArray_(argument) ? [...acc, ...argument] : [...acc, argument]),
    [],
  )
  return (customers, customerId) => arrayOfCustTypes.includes(get_(customers, [customerId, 'customerType']))
}


/*
 * *****************************************************************************
 * Misc
 * *****************************************************************************
*/

export function getDoesCustomerHaveAnyChildren(customers, customerId) {
  return getPropPassingInEntireCustomersSlice(customers, customerId, 'childrenIds').length > 0
}

export function getDoesCustomerHaveChild(customers, customerId, childCustomerId) {
  return getPropPassingInEntireCustomersSlice(customers, customerId, 'childrenIds').includes(childCustomerId)
}


/**
 * A helper function for other selectors in this file
 */
function getIdsOfAllCustomersWithCustomerInfoSavedForThem(customers) {
  return getPropOfAll(customers, 'id')
}

export function getIsCustomerInfoSavedForACustomer(customers, customerId) {
  getIdsOfAllCustomersWithCustomerInfoSavedForThem(customers).includes(customerId)
}

// If this is called on a MASTER customer, it should only be called after
// /customers/:id/children/objects has been successfully called on the MASTER
// customer. Returns an empty list if customer has no non-self-dist and pub
// children.
export function getIdsOfNonInactiveAndNonSelfDistAndPubChildren(customers, masterCustomerId) {
  if (!getIsCustomerOfATypeThatHasChildren(customers, masterCustomerId)) { return [] }
  const childrenIdsOfMaster = getPropPassingInEntireCustomersSlice(customers, masterCustomerId, 'childrenIds')
  const idAndCustomerTypeOfAllCustomers = getMultiplePropsOfAll(customers, 'id', 'customerType')
  const idAndCustomerTypeOfAllChildrenCustomers = idAndCustomerTypeOfAllCustomers.filter(
    idAndCustomerType => childrenIdsOfMaster.includes(idAndCustomerType.id),
  )
  const idAndCustomerTypeOfAllNonSelfDistAndPubChildren = idAndCustomerTypeOfAllChildrenCustomers.filter(
    idAndCustomerType => ![
      CUSTOMER_TYPES_PUB,
      CUSTOMER_TYPES_SELF_DISTRIBUTION,
    ].includes(idAndCustomerType.customerType),
  )

  const idsOfAllNonSelfDistAndPubChildren =
    idAndCustomerTypeOfAllNonSelfDistAndPubChildren.map(idAndCustomerType => idAndCustomerType.id)

  return idsOfAllNonSelfDistAndPubChildren.filter(id => !getIsCustomerStatusInactive(customers, id))
}

// Be careful with this function: you can only use it on rootCustomers or their
// children to get accurate results (you could get a false negative for
// relatedTo/relatedFrom customers); see CODE_COMMENTS_125.
export function getIsCustomerOnCreditHold(customers, customerId) {
  return (
    getPropPassingInEntireCustomersSlice(customers, customerId, 'customerStatus') === CUSTOMER_STATUS_HOLD
    && getPropPassingInEntireCustomersSlice(customers, customerId, 'customerStatus') === CUSTOMER_HOLD_TYPE_CREDIT_HOLD
  )
}

// Be careful with this function: you can only use it on rootCustomers or their
// children to get accurate results (you could get a false negative for
// relatedTo/relatedFrom customers); see CODE_COMMENTS_125.
export function getIsCustomerOnReportingHold(customers, customerId) {
  return (
    getPropPassingInEntireCustomersSlice(customers, customerId, 'customerStatus') === CUSTOMER_STATUS_HOLD
    && getPropPassingInEntireCustomersSlice(customers, customerId, 'customerStatus') === CUSTOMER_HOLD_TYPE_REPORTING_HOLD
  )
}

// CODE_COMMENTS_125
export function getIsCustomerStatusInactive(customers, customerId) {
  return getPropPassingInEntireCustomersSlice(customers, customerId, 'customerStatus') === CUSTOMER_STATUS_INACTIVE
}

export function sortArrayOfCustomerIdsByOtherCustomerProps({
  customers,
  customerIds,
  arrayOfPropNames,
  // optional: an array, the last argument of lodash's orderBy(), for example
  // ['asc', 'desc']
  ascendingAndDescendingRules,
}) {
  const props = getMultiplePropsOfAll(customers, 'id', ...arrayOfPropNames).filter(o => customerIds.includes(o.id))
  const sorted = ascendingAndDescendingRules
    ? orderBy_(props, arrayOfPropNames, ascendingAndDescendingRules)
    : sortBy_(props, arrayOfPropNames)
  return sorted.map(o => o.id)
}

// pass in an array of customerIds and this sends back an array of customer
// objects. If the customer doesn't exist in the store, it is skipped.
export function getMultipleCustomerObjectsById(customers, customerIds) {
  return customerIds.map(customerId => {
    const customerObj = get_(customers, customerId)
    return customerObj
  }).filter(customerObj => customerObj) // filter falsy (non-existent) objects
}


export function getBusinessUnitIdOfRootCustomer({
  entireCustomersSlice,
  entireCurrentUserSlice,
  // set this to a function. If the caller doesn't set this and the root
  // customer hasn't been fetched yet, a NestedPropNotInObjectError error will
  // be thrown.
  doThisRatherThanThrowErrorIfRootCustomerHasNotYetBeenFetched=null,
}) {
  let currentUserRootCustomerId
  let businessUnitIdOfCurrentUserRootCustomer
  try {
    currentUserRootCustomerId = entireCurrentUserSlice.rootCustomerId
    businessUnitIdOfCurrentUserRootCustomer = getPropPassingInEntireCustomersSlice(
      entireCustomersSlice,
      currentUserRootCustomerId,
      'businessUnitId',
    )
  } catch (e) {
    if (e instanceof NestedPropNotInObjectError) {
      if (doThisRatherThanThrowErrorIfRootCustomerHasNotYetBeenFetched) {
        return doThisRatherThanThrowErrorIfRootCustomerHasNotYetBeenFetched({
          entireCustomersSlice,
          entireCurrentUserSlice,
          currentUserRootCustomerId,
        })
      }
      throw e
    }
    throw e
  }
  // If for whatever reason the `businessUnitId` prop of the root customer is
  // set to null within the API object, set it to MicroStar
  if (!businessUnitIdOfCurrentUserRootCustomer) {
    businessUnitIdOfCurrentUserRootCustomer = BUSINESS_UNIT_ID_TYPE_MICROSTAR
  }

  return businessUnitIdOfCurrentUserRootCustomer
}


// CODE_COMMENTS_228
export function getIsRentalCustomer(customers, customerId) {
  const customerType = get_(customers, [customerId, 'customerType'])
  // It's possible a Master customer could have a name that starts with 'KCR'
  // (see CODE_COMMENTS_228), in which case we want to treat that customer as a
  // Master, not as a Rental Customer.
  if (customerType === CUSTOMER_TYPES_MASTER) {
    return false
  }
  return getPropPassingInEntireCustomersSlice(customers, customerId, 'name')?.toLowerCase()?.startsWith('kcr')
}

/*
 * *****************************************************************************
 * Customer Reps
 * *****************************************************************************
*/

// returns an array (IDs are not mapped to rep type), nulls values filtered out
export function getIdsOfAllReps(customers, customerId) {
  return deDupeArray([
    getPropPassingInEntireCustomersSlice(customers, customerId, 'ceRep'),
    getPropPassingInEntireCustomersSlice(customers, customerId, 'logisticsRep'),
    getPropPassingInEntireCustomersSlice(customers, customerId, 'accountRep'),
    getPropPassingInEntireCustomersSlice(customers, customerId, 'qualityRep'),
    getPropPassingInEntireCustomersSlice(customers, customerId, 'salesRep'),
  ].filter(
    // If a customer doesn't have, say, a sales rep assigned to them, the
    // 'salesRep' prop will be null.
    id => id,
  ))
}

export function getAllRepsOfCustomer(customers, customerId, filterOutNulls=true) {
  const toReturn = {
    [CUSTOMER_REPS_CUSTOMER_EXPERIENCE]: getPropPassingInEntireCustomersSlice(customers, customerId, 'ceRep'),
    [CUSTOMER_REPS_LOGISTICS]: getPropPassingInEntireCustomersSlice(customers, customerId, 'logisticsRep'),
    [CUSTOMER_REPS_ACCOUNT]: getPropPassingInEntireCustomersSlice(customers, customerId, 'accountRep'),
    [CUSTOMER_REPS_QUALITY]: getPropPassingInEntireCustomersSlice(customers, customerId, 'qualityRep'),
    [CUSTOMER_REPS_SALES]: getPropPassingInEntireCustomersSlice(customers, customerId, 'salesRep'),
  }
  if (filterOutNulls) {
    return Object.keys(toReturn).reduce((acc, key) => (
      toReturn[key]
        ? { ...acc, [key]: toReturn[key] }
        : acc
    ), {})
  }
  return toReturn
}

export function getAllNonNullRepTypesOfCustomer(customers, customerId) {
  const allReps = getAllRepsOfCustomer(customers, customerId)
  return Object.keys(allReps)
}
